import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/InstagramAnalyticsPostDetails.graphql';
import { Query, QueryinstagramAnalyticsPostDetailsArgs } from '../../__generated__/globalTypes';

export const useInstagramAnalyticsPostDetailsQuery = (
  options?: QueryHookOptions<Query, QueryinstagramAnalyticsPostDetailsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryinstagramAnalyticsPostDetailsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useInstagramAnalyticsPostDetailsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryinstagramAnalyticsPostDetailsArgs>
): QueryResult<Query, QueryinstagramAnalyticsPostDetailsArgs> & {
  getInstagramAnalyticsPostDetails: LazyQueryExecFunction<Query, QueryinstagramAnalyticsPostDetailsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getInstagramAnalyticsPostDetails, result] = useLazyQuery<Query, QueryinstagramAnalyticsPostDetailsArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getInstagramAnalyticsPostDetails, ...result };
};

export const useInstagramAnalyticsPostDetailsPromiseQuery = () => {
  const client = useApolloClient();

  const getInstagramAnalyticsPostDetails = useCallback(
    (options?: Omit<QueryOptions<QueryinstagramAnalyticsPostDetailsArgs, Query>, 'query'>) =>
      client.query<Query, QueryinstagramAnalyticsPostDetailsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getInstagramAnalyticsPostDetails };
};
// Suspense Query Hooks
export const useInstagramAnalyticsPostDetailsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryinstagramAnalyticsPostDetailsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryinstagramAnalyticsPostDetailsArgs>(QUERY, {
    ...options
  });

  return result;
};
